import grooveData from '@/data/grooves.json';

const zeroBasedLayer = (layer) => ({
  ...layer,
  steps: (layer.steps || []).map((step) => Number(step) - 1),
  soft: (layer.soft || []).map((step) => Number(step) - 1),
  ratchets: Object.fromEntries(Object.entries(layer.ratchets || {}).map(([step, count]) => [Number(step) - 1, Number(count)])),
});

export const GROOVES = Object.freeze((grooveData.grooves || []).map((groove) => ({
  ...groove,
  tempo: [...groove.tempo],
  layers: (groove.layers || []).map(zeroBasedLayer),
  challenge: { ...groove.challenge },
  grooveBars: 1,
})));

export const grooveById = (id) => GROOVES.find((groove) => groove.id === id) || null;

const inTempo = (groove, bpm) => Number(bpm) >= groove.tempo[0] && Number(bpm) <= groove.tempo[1];
export const grooveFitsTempo = (groove, bpm) => inTempo(groove, bpm) || inTempo(groove, Number(bpm) / 2);

const tempoDistance = (groove, bpm) => {
  const center = (groove.tempo[0] + groove.tempo[1]) / 2;
  return Math.min(Math.abs(Number(bpm) - center), Math.abs(Number(bpm) / 2 - center));
};

/** Full picker model: tempo fits first, then every remaining groove grouped by family. */
export function groovesForTempo(bpm) {
  const sorted = [...GROOVES].sort((a, b) => tempoDistance(a, bpm) - tempoDistance(b, bpm) || a.name.localeCompare(b.name));
  const fits = sorted.filter((groove) => grooveFitsTempo(groove, bpm));
  const fitIds = new Set(fits.map((groove) => groove.id));
  const families = {};
  for (const groove of sorted) {
    if (fitIds.has(groove.id)) continue;
    (families[groove.family] ||= []).push(groove);
  }
  return { fitsLabel: `Fits ${Number(bpm)} BPM`, fits, families };
}

/** Expand a half-time groove over two session bars without changing its grid. */
export function adaptGroove(groove, bpm) {
  if (!groove || inTempo(groove, bpm) || !inTempo(groove, Number(bpm) / 2)) return groove;
  return {
    ...groove,
    adapted: true,
    grooveBars: 2,
    layers: groove.layers.map((layer) => ({
      ...layer,
      steps: layer.steps.map((step) => step * 2),
      soft: layer.soft.map((step) => step * 2),
      ratchets: Object.fromEntries(Object.entries(layer.ratchets).map(([step, count]) => [Number(step) * 2, count])),
    })),
  };
}

const rowSet = (rows) => rows instanceof Set ? rows : new Set((rows || []).map((row) => typeof row === 'string' ? row : row?.rowId).filter(Boolean));

/** Turn a normalized groove into drum-pattern arrays, repeating its one/two-bar phrase. */
export function grooveToSteps(groove, rows, patternBars) {
  const available = rowSet(rows);
  const bars = Math.max(Number(patternBars) || 1, groove?.grooveBars || 1);
  const stepsPerBar = groove?.stepsPerBar === 12 ? 12 : 16;
  const phraseBars = groove?.grooveBars || 1;
  const phraseLength = phraseBars * stepsPerBar;
  const length = bars * stepsPerBar;
  const steps = Object.fromEntries([...available].map((row) => [row, Array(length).fill(0)]));
  const ratchets = Object.fromEntries([...available].map((row) => [row, {}]));
  const missingRows = [...new Set((groove?.layers || []).map((layer) => layer.row).filter((row) => !available.has(row)))];
  for (const layer of groove?.layers || []) {
    if (!available.has(layer.row)) continue;
    const soft = new Set(layer.soft || []);
    const layerHits = new Set([...(layer.steps || []), ...Object.keys(layer.ratchets || {}).map(Number)]);
    for (let offset = 0; offset < length; offset += phraseLength) {
      for (const hit of layerHits) {
        const index = offset + hit;
        if (index >= length) continue;
        steps[layer.row][index] = Math.max(steps[layer.row][index], soft.has(hit) ? 0.55 : 1);
        const count = layer.ratchets?.[hit];
        if (count) ratchets[layer.row][index] = Math.max(ratchets[layer.row][index] || 1, count);
      }
    }
  }
  return { steps, ratchets, missingRows };
}

/** Rows whose selected sound is present on the selected manifest kit. Muting does not matter. */
export function kitRowAvailability(pattern, manifest) {
  const kit = (manifest?.drumKits || []).find((candidate) => candidate.id === pattern?.kitId);
  const selected = new Map((pattern?.rows || []).map((row) => [row.rowId, row.soundId]));
  return new Set((kit?.rows || []).filter((row) => (row.sounds || []).some((sound) => sound.id === selected.get(row.id))).map((row) => row.id));
}

export const __grooveDataVersion = grooveData.version;
